import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { isNumericInt } from 'utils/validations';
import { useMutation } from '@apollo/client';
import { createWaybillPackageSizeTemplate } from 'apollo/templates/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { WaybillPackageSize } from 'models/transport';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';

type AddPackageSizeDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    onAdd: (size: WaybillPackageSize) => void;
};
const AddPackageSizeDialog: FC<AddPackageSizeDialogProps> = ({
    open,
    onDialogClose,
    onAdd,
}: AddPackageSizeDialogProps) => {
    const { t: translate } = useTranslation(['common', 'lab-settings']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const { showBoundary } = useDenthubErrorBoundary();

    const [createWaybillPackageSize] = useMutation(createWaybillPackageSizeTemplate, {
        onCompleted: ({ createWaybillPackageSize }) => {
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('common:CHANGES_SAVED') });
            onAdd(createWaybillPackageSize);
            formik.resetForm();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: WaybillPackageSize) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('common:VALIDATION:FIELD_REQUIRED');
        }
        if (!values.width || values.width === 0) {
            errors.width = translate('common:VALIDATION:FIELD_REQUIRED');
        }
        if (!values.height || values.width === 0) {
            errors.height = translate('common:VALIDATION:FIELD_REQUIRED');
        }
        if (!values.length || values.width === 0) {
            errors.length = translate('common:VALIDATION:FIELD_REQUIRED');
        }
        if (values.width && !isNumericInt(values.width.toString())) {
            errors.width = translate('common:VALIDATION:FIELD_NUMERIC_INT');
        }
        if (values.height && !isNumericInt(values.height.toString())) {
            errors.height = translate('common:VALIDATION:FIELD_NUMERIC_INT');
        }
        if (values.length && !isNumericInt(values.length.toString())) {
            errors.length = translate('common:VALIDATION:FIELD_NUMERIC_INT');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            width: 0,
            height: 0,
            length: 0,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: WaybillPackageSize) => {
            createWaybillPackageSize({
                variables: {
                    packageSize: {
                        name: values.name,
                        width: parseInt(values.width.toString()),
                        height: parseInt(values.height.toString()),
                        length: parseInt(values.length.toString()),
                    },
                },
            });
        },
    });

    const handleDialogClose = () => {
        formik.resetForm();
        onDialogClose();
    };

    return (
        <DHDialog
            fullWidth
            maxWidth="md"
            open={open}
            onClose={handleDialogClose}
            title={translate('lab-settings:ADD_PACKAGE_SIZE')}
            actions={
                <LoadingButton
                    disabled={!formik.isValid}
                    variant="contained"
                    type="submit"
                    onClick={() => formik.submitForm()}
                >
                    {translate('common:ADD')}
                </LoadingButton>
            }
        >
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                label={translate('common:NAME')}
                                required
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                label={translate('lab-settings:WIDTH')}
                                required
                                helperText={formik.touched.width && formik.errors.width}
                                error={formik.touched.width && Boolean(formik.errors.width)}
                                {...formik.getFieldProps('width')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                label={translate('lab-settings:HEIGHT')}
                                required
                                helperText={formik.touched.height && formik.errors.height}
                                error={formik.touched.height && Boolean(formik.errors.height)}
                                {...formik.getFieldProps('height')}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                label={translate('lab-settings:LENGTH')}
                                required
                                helperText={formik.touched.length && formik.errors.length}
                                error={formik.touched.length && Boolean(formik.errors.length)}
                                {...formik.getFieldProps('length')}
                            />
                        </FormControl>
                    </Grid>
                </Grid>
            </form>
        </DHDialog>
    );
};

export default AddPackageSizeDialog;
